import { BOGUS_SELECTOR } from '@/utils/editor/textBoxPaint'
import { settleTextBoxFaces } from '@/utils/editor/textBoxFaces'

/** The class TinyMCE gives the element it edits inline, which the global content rule styles. */
const TINYMCE_CONTENT_BODY_CLASS = 'mce-content-body'

/** A TinyMCE text item to measure: its id, its stored HTML and the width its box paints at. */
export type EditorTurnoverTextItem = {
  id: string
  html: string
  width: number
}

/**
 * Where the measurements are laid out: in the document, so the global
 * TinyMCE rule (`styles/tinyMCEStyles.ts`) styles them as it styles a box on
 * the canvas, the preview and the PDF, but off screen and invisible.
 */
const STAGE_STYLE = [
  'position: absolute',
  'top: 0',
  'left: -100000px',
  'visibility: hidden',
  'pointer-events: none',
].join('; ')

/** Attributes whose value is a URL a browser may navigate to or load. */
const URL_ATTRIBUTES = [
  'href',
  'xlink:href',
  'src',
  'action',
  'formaction',
  'data',
  'poster',
  'background',
]

/** SVG animation attributes that can set a URL attribute's value. */
const ANIMATION_VALUE_ATTRIBUTES = ['to', 'from', 'values', 'by']

/**
 * Elements that come out before the stored HTML reaches the live document: a
 * script runs code, and a meta, a base or a link acts on the whole page (a
 * meta can refresh or navigate it, a base rebases every relative URL in it,
 * a link can load a stylesheet into it or fetch a resource). A style element
 * stays, as TinyMCE's parser keeps it under the editor's schema, because its
 * rules can change the box's laid-out height.
 */
const PAGE_WIDE_SELECTOR = 'script, meta, base, link'

/** Stands in for a refused URL: it loads nothing and runs nothing. */
const INERT_URL = 'about:invalid'

/**
 * Whether a URL runs code when the browser follows it, read as the browser
 * reads a URL: case-insensitively, and ignoring the control characters and
 * spaces it strips out. An animation value list runs code when any entry
 * does.
 */
const runsCode = (value: string, isList: boolean): boolean => {
  // eslint-disable-next-line no-control-regex
  const url = value.replace(/[\u0000-\u0020]/g, '').toLowerCase()
  return isList ? url.includes('javascript:') : url.startsWith('javascript:')
}

/**
 * The stored HTML as nodes ready to lay out, without its script, meta, base
 * and link elements, event-handler attributes, srcdoc attributes and
 * javascript: URLs, and
 * without TinyMCE's bogus scaffolding (`BOGUS_SELECTOR`), which
 * its serializer would drop. It is parsed in a document of its own, which
 * has no window, so nothing runs or loads while it is cleaned, and parsed
 * under a body, as the measuring element would parse it, so every element
 * lands where it would. Script, meta, base and link elements go, every `on*`
 * and `srcdoc` attribute goes, and a URL attribute that runs code takes an
 * inert URL in its place; every element, class and style that lays the text
 * out stays, style elements among them.
 * The nodes are moved, never serialized and parsed again, so the measuring
 * element receives exactly the tree that was cleaned.
 */
const measurableNodesOf = (html: string): Node[] => {
  const inert = document.implementation.createHTMLDocument('').body
  inert.innerHTML = html
  inert
    .querySelectorAll(PAGE_WIDE_SELECTOR)
    .forEach((element) => element.remove())
  inert.querySelectorAll(BOGUS_SELECTOR).forEach((bogus) => bogus.remove())
  inert.querySelectorAll('*').forEach((element) => {
    for (const name of element.getAttributeNames()) {
      const lower = name.toLowerCase()
      const value = element.getAttribute(name) ?? ''
      if (lower.startsWith('on') || lower === 'srcdoc')
        element.removeAttribute(name)
      else if (
        (URL_ATTRIBUTES.includes(lower) && runsCode(value, false)) ||
        (ANIMATION_VALUE_ATTRIBUTES.includes(lower) && runsCode(value, true))
      )
        element.setAttribute(name, INERT_URL)
    }
  })
  return [...inert.childNodes]
}

/**
 * The height each item's stored HTML paints at, laid out off screen the way
 * a TinyMCE box paints it on every surface: a body under the content rule's
 * class (`mce-content-body`) inside a frame as wide as the box, read as the
 * body's own height once the faces its text uses have loaded. An item whose
 * text lays out at no height is left out, so its box is not converted.
 */
export const measureEditorTurnoverTextHeights = async (
  items: readonly EditorTurnoverTextItem[],
): Promise<Record<string, number>> => {
  if (items.length === 0) return {}
  const stage = document.createElement('div')
  stage.setAttribute('data-editor-turnover-measure', 'true')
  stage.style.cssText = STAGE_STYLE
  const bodies = items.map((item) => {
    const frame = document.createElement('div')
    frame.style.cssText = `position: relative; width: ${item.width}px`
    const body = document.createElement('div')
    body.className = TINYMCE_CONTENT_BODY_CLASS
    // The measurement strips scripts, meta, base and link elements, event
    // handlers, srcdoc and javascript: URLs from the stored HTML.
    body.replaceChildren(...measurableNodesOf(item.html))
    frame.appendChild(body)
    stage.appendChild(frame)
    return { id: item.id, body }
  })
  document.body.appendChild(stage)
  try {
    // A height read before the faces the text uses land is the fallback face's.
    const settling = settleTextBoxFaces(stage)
    if (settling !== null) await settling
    const heights: Record<string, number> = {}
    for (const { id, body } of bodies) {
      const height = body.offsetHeight
      if (height > 0) heights[id] = height
    }
    return heights
  } finally {
    stage.remove()
  }
}
